// src/pages/Dashboard.jsx
import { useEffect, useMemo, useState, useContext } from "react";
import { signOut } from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase/firebase";
import { AuthContext } from "../context/AuthContext";
import "./DocsHub.css";

function canSeeFolder(folder, userData) {
  if (userData?.role === "admin") return true;
  const allowed = folder.allowedGroups || [];
  const userGroups = userData?.groups || [];
  return allowed.some((g) => userGroups.includes(g));
}

export default function Dashboard({ onAdminClick, onResourcePanelClick, onStudentPanelClick }) {
  const { userData } = useContext(AuthContext);

  const [allFolders, setAllFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "folders"));
      const folders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const visible = folders.filter((f) => canSeeFolder(f, userData));
      setAllFolders(visible);
      setCurrentFolderId(null);
      setDocuments([]);
    })().catch(console.error);
  }, [userData]);

  const folderById = useMemo(() => {
    const m = new Map();
    allFolders.forEach((f) => m.set(f.id, f));
    return m;
  }, [allFolders]);

  const children = useMemo(() => {
    return allFolders.filter((f) => (f.parentId || null) === (currentFolderId || null));
  }, [allFolders, currentFolderId]);

  const breadcrumb = useMemo(() => {
    const chain = [];
    let cur = currentFolderId ? folderById.get(currentFolderId) : null;
    while (cur) {
      chain.push(cur);
      cur = cur.parentId ? folderById.get(cur.parentId) : null;
    }
    return chain.reverse();
  }, [currentFolderId, folderById]);

  const loadDocs = async (folderId) => {
    setLoadingDocs(true);
    setDocuments([]);
    try {
      const folderRef = ref(storage, `folders/${folderId}`);
      const fileList = await listAll(folderRef);
      const docs = await Promise.all(
        fileList.items.map(async (item) => ({
          name: item.name,
          url: await getDownloadURL(item),
        }))
      );
      setDocuments(docs);
    } catch (e) {
      console.error(e);
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const openFolder = async (folderId) => {
    setCurrentFolderId(folderId);
    await loadDocs(folderId);
  };

  const goToCrumb = async (folderIdOrNull) => {
    setCurrentFolderId(folderIdOrNull);
    if (folderIdOrNull) await loadDocs(folderIdOrNull);
    else setDocuments([]);
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  return (
    <div className="content-area">
      <div className="docs-shell">
        {/* Page header (reuses learner page patterns from App.css) */}
        <div className="docs-header-card">
          <div className="page-header" style={{ marginBottom: 0 }}>
            <div className="page-title">📁 Learning Resources</div>
            <div className="page-subtitle">
              Browse folders and subfolders based on your access level.
            </div>
          </div>

          <div className="docs-header-actions">
            <div className="docs-user-pill">
              <span>{userData?.email}</span>
              <span className="docs-role-badge">{userData?.role || "user"}</span>
            </div>
            {(userData?.role === "admin" || userData?.role === "uploader") && (
              <button className="btn btn-primary" onClick={onResourcePanelClick}>
                ⚙️ Resource Panel
              </button>
            )}
            {userData?.role === "admin" && (
              <button className="btn btn-primary" onClick={onStudentPanelClick}>
                ⚙️ Student Panel
              </button>
            )}

            {userData?.role === "admin" && (
              <button className="btn btn-primary" onClick={onAdminClick}>
                ⚙️ Admin Panel
              </button>
            )}
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        <div style={{ height: 16 }} />

        <div className="docs-grid">
          {/* Left: folders */}
          <div className="docs-panel">
            <div className="docs-panel-title">Folders</div>

            <div className="docs-breadcrumb">
              <span style={{ fontWeight: 800, color: "var(--color-secondary)" }}>Path:</span>
              <button className="docs-crumb-btn" onClick={() => goToCrumb(null)}>
                Root
              </button>
              {breadcrumb.map((f) => (
                <span key={f.id} style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <span>/</span>
                  <button className="docs-crumb-btn" onClick={() => goToCrumb(f.id)}>
                    {f.name}
                  </button>
                </span>
              ))}
            </div>

            {children.length === 0 ? (
              <div className="docs-info">No subfolders here.</div>
            ) : (
              <div className="docs-cards-row">
                {children.map((f) => (
                  <div
                    key={f.id}
                    className={`docs-card ${currentFolderId === f.id ? "selected" : ""}`}
                    onClick={() => openFolder(f.id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="docs-card-name">📁 {f.name}</div>
                    <div className="docs-card-meta">
                      {f.allowedGroups?.length ? `Access: ${f.allowedGroups.join(", ")}` : "Access: None"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: documents */}
          <div className="docs-panel">
            <div className="docs-panel-title">Documents</div>

            {!currentFolderId ? (
              <div className="docs-info">Select a folder to view documents.</div>
            ) : loadingDocs ? (
              <div className="docs-info">Loading documents…</div>
            ) : documents.length === 0 ? (
              <div className="docs-info">No documents in this folder.</div>
            ) : (
              <div className="docs-list">
                {documents.map((d) => (
                  <div className="docs-item" key={d.name}>
                    <div className="docs-item-name">📄 {d.name}</div>
                    <a className="docs-link" href={d.url} target="_blank" rel="noreferrer">
                      View →
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
